/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import { useEffect, useMemo, useState } from "react";
import Plot from "../../../../plotly/Plot";
import { isFilledArray } from "../../../../utils";
import { dayjs } from "../../../../utils/date";
import { Loading } from "../../../Helper/Loading";
import useRipeningRoom from "../hooks/useRipeningRoom";
import mocked_avocado from "./mocked-avocado.json";
import mocked_banana from "./mocked-banana.json";
import mocked_mango from "./mocked-mango.json";
import { AXIS_GROUP_PRIORITY, AXIS_GROUPS } from "./axisGroups";
import { ripeningGraphXAxis } from "./ripeningGraphConstants";
import { clampSmaWindow, simpleMovingAverage } from "./smoothing";

const mocked_data = {
    avocado: mocked_avocado,
    mango: mocked_mango,
    banana: mocked_banana,
};

// Sensor configuration with colors and axis-group assignments
const SENSOR_CONFIG = {
    temperature_sensor_1: {
        name: "Temperature 1",
        color: "#d62728",
        unit: "°C",
        group: "temperature",
    },
    temperature_sensor_2: {
        name: "Temperature 2",
        color: "#9467bd",
        unit: "°C",
        group: "temperature",
    },
    co2_value: {
        name: "CO₂",
        color: "#ff7f0e",
        unit: "%",
        group: "co2",
    },
    c2h4_value: {
        name: "C₂H₄",
        color: "#2ca02c",
        unit: "ppm",
        group: "c2h4",
    },
    o2_value: {
        name: "O₂",
        color: "#1f77b4",
        unit: "%",
        group: "o2",
    },
} as const satisfies Record<string, { name: string; color: string; unit: string; group: string }>;


const DEFAULT_COLORS = ["#d62728", "#9467bd", "#ff7f0e", "#2ca02c", "#1f77b4", "#17becf", "#8c564b", "#bcbd22"];

function formatKeyFallbackLabel(key: string) {
    return key
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase());
}

type SensorTraceConfig = {
    name: string;
    color: string;
    unit: string;
    group: string;
};

function getConfigForKey(sensorKey: string, label: string, index: number, overrideColor?: string): SensorTraceConfig {
    if (SENSOR_CONFIG[sensorKey]) {
        return {
            ...SENSOR_CONFIG[sensorKey],
            name: label || SENSOR_CONFIG[sensorKey].name,
            color: overrideColor || SENSOR_CONFIG[sensorKey].color,
        };
    }
    return {
        name: label || formatKeyFallbackLabel(sensorKey),
        color: overrideColor || DEFAULT_COLORS[index % DEFAULT_COLORS.length],
        unit: "",
        group: "default",
    };
}

function createScatterSeries(
    timestamps: string[],
    values: number[],
    config: { name: string; color: string; unit: string },
    yaxis: string,
) {
    return {
        type: "scatter",
        x: timestamps,
        y: values,
        mode: "lines+markers",
        name: config.name,
        yaxis,

        marker: {
            color: config.color,
            size: 6,
        },
        line: {
            color: config.color,
            width: 2,
            shape: "spline",
            smoothing: 1.3
        },

        hovertemplate:
        `<b>${config.name}</b><br>`
        + `%{y:.2f} ${config.unit}<br>`
        + `%{x}<br>`
        + `<extra></extra>`,
    };
}

export default function SensorDataGraph() {
    const { registerDataSeries, fruit_type, availableRegisterKeys, sensorGraphConfig, currentLocation } = useRipeningRoom();
    const [isLoading, setIsLoading] = useState(true);
    const selectedSensorConfig = sensorGraphConfig || [];
    const selectedSensorKeys = selectedSensorConfig.map((i: any) => i.key) as string[];
    const labelByKey = useMemo(
        () => (availableRegisterKeys || []).reduce((acc, item) => ({ ...acc, [item.key]: item.label }), {}),
        [availableRegisterKeys]
    );

    useEffect(() => {
        if (registerDataSeries !== undefined) {
            setIsLoading(false);
        }
    }, [registerDataSeries]);

    // Check if we have ANY backend sensor data
    const hasBackendData = registerDataSeries && Object.keys(registerDataSeries).some(
        (key) => isFilledArray(registerDataSeries[key])
    );

    // * Mocked data is only for rooms linked to the "mocked" gateway; every other room shows only its own data.
    const usesMockedData = currentLocation?.gateway === "mocked";


    // Create plot data series + figure out which axis groups are active
    const [plotData, maxValues, orderedGroups] = useMemo<[
        any[],
        Record<string, number>,
        string[],
    ]>(() => {
        if (isLoading) {
            return [[], {}, []];
        }

        const maxValues: Record<string, number> = selectedSensorKeys.reduce((acc, key) => {
            acc[key] = 0;
            return acc;
        }, {} as Record<string, number>);

        // First pass: collect raw series for sensors that actually have data.
        // We delay axis assignment until we know which groups are populated,
        // so the *first* axis ("y") is always backed by real data — Plotly
        // misbehaves when the primary y-axis is empty.
        const rawSeries: Array<{
            sensorKey: string;
            traceConfig: SensorTraceConfig;
            timestamps: string[];
            values: number[];
        }> = [];

        if (!hasBackendData && usesMockedData) {
            // No backend data on a mocked room → show mocked data
            selectedSensorConfig.forEach((cfg: any, index: number) => {
                const sensorKey = cfg.key;
                const traceConfig = getConfigForKey(sensorKey, cfg?.label || labelByKey[sensorKey], index, cfg?.color);
                const mockedValues = mocked_data[fruit_type as string]?.[sensorKey];
                if (mockedValues && mockedValues.length > 0) {
                    const timestamps = mockedValues.map(
                        (_, i) => new Date(Date.now() - (mockedValues.length - i) * 3600000).toISOString()
                    );
                    maxValues[sensorKey] = Math.max(...mockedValues);

                    const windowSize = clampSmaWindow(cfg?.sma_n);
                    const values = windowSize ? (simpleMovingAverage(mockedValues, windowSize) as any) : mockedValues;
                    rawSeries.push({ sensorKey, traceConfig, timestamps, values });
                }
            });
        } else if (hasBackendData) {
            // Has backend data → only show sensors that actually have values
            selectedSensorConfig.forEach((cfg: any, index: number) => {
                const sensorKey = cfg.key;
                const traceConfig = getConfigForKey(sensorKey, cfg?.label || labelByKey[sensorKey], index, cfg?.color);
                const sensorData = registerDataSeries[sensorKey];
                if (!isFilledArray(sensorData)) {
                    return;
                }

                const timestamps = sensorData.map((pair: [string, number]) => dayjs.utc(pair[0]).local().format());
                const rawValues = sensorData.map((pair: [string, number]) => pair[1]);

                maxValues[sensorKey] = Math.max(...rawValues);

                const windowSize = clampSmaWindow(cfg?.sma_n);
                const values = windowSize ? simpleMovingAverage(rawValues, windowSize) : rawValues;
                rawSeries.push({ sensorKey, traceConfig, timestamps, values: values as any });
            });
        }

        // Determine which axis groups have any data, sorted by priority
        // (temperature first, then c2h4, o2, co2, default). The first one
        // becomes "y", the rest overlay it.
        const activeGroups = new Set<string>(rawSeries.map((s) => s.traceConfig.group));
        const orderedGroups = AXIS_GROUP_PRIORITY.filter((g) => activeGroups.has(g));

        const groupToAxis: Record<string, string> = {};
        orderedGroups.forEach((group, i) => {
            groupToAxis[group] = i === 0 ? "y" : `y${i + 1}`;
        });

        const plotData = rawSeries.map(({ traceConfig, timestamps, values }) => {
            const yaxis = groupToAxis[traceConfig.group] ?? "y";
            return createScatterSeries(timestamps, values, traceConfig, yaxis);
        });

        return [plotData, maxValues, orderedGroups];
    }, [registerDataSeries, fruit_type, hasBackendData, usesMockedData, isLoading, selectedSensorKeys, selectedSensorConfig, labelByKey]);

    if (isLoading) {
        return <Loading />;
    }

    // * Nothing to draw (no data, or a mocked room without mocked data for its fruit type): say so instead of an empty chart.
    if (plotData.length === 0) {
        return (
            <div className="text-center d-flex align-items-center justify-content-center"
                css={css`min-height: 45rem; background-color: #f8f8f8;`}>
                <p className="text-muted mb-0">
                    No sensor data available<br />
                    Sensor data will appear here when available
                </p>
            </div>
        );
    }

    console.log("maxValues", maxValues); // eslint-disable-line no-console

    // Build yaxis* layout entries only for the groups that actually have data.
    // The first active group is the primary axis ("yaxis"), the rest overlay
    // it on the right side ("yaxis2", "yaxis3", ...).
    const yAxisLayout: Record<string, any> = {};
    orderedGroups.forEach((group, i) => {
        const groupConfig = AXIS_GROUPS[group] ?? AXIS_GROUPS.default;
        const axisKey = i === 0 ? "yaxis" : `yaxis${i + 1}`;
        yAxisLayout[axisKey] = {
            title: groupConfig.title,
            side: i === 0 ? "left" : "right",
            showgrid: false,
            visible: false,
            ...(groupConfig.range ? { range: groupConfig.range } : {}),
            ...(i > 0 ? { overlaying: "y" } : {}),
        };
    });

    const layout = {
        autosize: true,
        showlegend: true,
        dragmode: false,

        legend: {
            orientation: "h",
            y: 1.08,
            x: 0.5,
            xanchor: "center",
        },

        margin: {
            l: 40,
            r: 40,
            t: 50,
            b: 50,
        },

        ...yAxisLayout,

        // Same time axis as the other ripening graphs, but this one stays zoomable.
        xaxis: { ...ripeningGraphXAxis(plotData), fixedrange: false },

        hovermode: "x unified",

        plot_bgcolor: "white",
        paper_bgcolor: "white",
    } as any;

    return (
        <div>
            <div css={css`
                min-height: 45rem;
                .plotly .cursor-crosshair,
                .plotly .cursor-drag,
                .plotly .draglayer,
                .plotly .nsewdrag,
                .plotly .ewdrag,
                .plotly .nsdrag {
                    cursor: crosshair !important;
                }
            `}>
                <Plot
                    data={plotData}
                    layout={layout}

                    useResizeHandler={true}
                    style={{ width: "100%", height: "100%", minHeight: "45rem" }}
                    config={{
                        displayModeBar: false,
                        displaylogo: false,
                        responsive: true,
                        scrollZoom: false,
                        doubleClick: false,
                        doubleClickDelay: 0,
                    }}
                />
            </div>
        </div>
    );
}
